import {DepositoryType, TransferType} from '@transactions/graphql';

/**
 * One signed leg of the transfer about to be created, with the account it
 * moves through. `amount` is the *allocated* amount, not the raw's full
 * amount: a transfer may consume its raws partially, and it is the allocated
 * legs that decide whether the thing balances and which way it runs.
 *
 * The account shape is structurally satisfied by the account union on
 * BasicRawTransactionFragment, which already carries depositoryType.
 */
export type TransferLeg = {
  amount: number;
  account:
    | {__typename: 'DepositoryAccount'; _id: string; depositoryType: DepositoryType}
    | {__typename: 'InvestmentAccount'; _id: string};
};

const BANK_ACCOUNT_TYPES: DepositoryType[] = [
  DepositoryType.Checking,
  DepositoryType.Savings,
];

/**
 * Infer a transfer type from structural signals alone — account types plus
 * amount direction — with no historical data.
 *
 * This runs client-side because the selection already carries everything it
 * needs: the legs' allocated amounts and, through the account union, each
 * account's depositoryType. Reading the allocations is only possible here —
 * the server's suggester took raw ids alone and never saw them, so it could
 * not type a partial transfer at all.
 *
 * Only the one unambiguous case is encoded — a credit card and a bank account
 * moving money in the payment direction. Everything else (bank↔bank, same
 * account, reverse direction) returns null and is left to the user: telling a
 * refund from a reimbursement from a prepaid reload takes knowledge the
 * selection's shape simply doesn't carry.
 */
export const inferTransferTypeFromAccounts = (
  transferLegs: TransferLeg[],
): TransferType | null => {
  // Only simple two-leg transfers, one leg per account. With 3+ legs the
  // extras (fees, partial refunds) make the grouping ambiguous, and a
  // same-account pair could be anything.
  if (transferLegs.length !== 2) return null;
  if (transferLegs[0].account._id === transferLegs[1].account._id) return null;
  // Legs that don't net to zero aren't a transfer at all.
  if (transferLegs[0].amount + transferLegs[1].amount !== 0) return null;

  const legs = transferLegs.map((leg) => ({
    amount: leg.amount,
    type:
      leg.account.__typename === 'DepositoryAccount'
        ? leg.account.depositoryType
        : null,
  }));

  // credit_card_payment: the bank leg pays out, the card leg pays down. The
  // direction is what separates it from a cash advance or a reversal.
  const cardPaidDown = legs.some(
    (leg) => leg.type === DepositoryType.CreditCard && leg.amount > 0,
  );
  const bankPaidOut = legs.some(
    (leg) => leg.type != null && BANK_ACCOUNT_TYPES.includes(leg.type) && leg.amount < 0,
  );

  return cardPaidDown && bankPaidOut ? TransferType.CreditCardPayment : null;
};
